import type { PrefKey, Preferences } from './types';

export const DEFAULT_BRIEF_OPTIONS: Partial<Record<PrefKey, readonly string[]>> = {
  purpose: ['Self-use', 'Investment', 'Not sure yet'],
  goal: ['Capital appreciation', 'Rental income', 'Diversification', 'Wealth preservation'],
  budget: ['₹40–50L', '₹50–70L', '₹70L–1 Cr', '₹1 Cr+'],
  bhk: ['1 BHK', '2 BHK', '3 BHK', '4+ BHK'],
  propertyType: ['Apartment', 'Villa', 'Plot / land', 'Planted estate'],
  plotSize: ['1200 sqft', '1500 sqft', '2400 sqft', 'Quarter acre', 'Half acre'],
  location: ['Whitefield', 'Sarjapur / ORR', 'North Bangalore', 'Open to suggestions'],
  timeline: ['Next 3 months', 'Next 6 months', '6–12 months', 'Exploring'],
  horizon: ['Under 3 years', '3–5 years', '5–10 years', 'Long hold'],
  schools: ['Yes, factor them in', 'Not a priority'],
  hub: ['Whitefield / ITPL', 'Electronic City', 'Manyata / North', 'MG Road / CBD', 'Not commute-driven'],
  worries: [
    'Overpaying',
    'Hidden costs',
    'Daily traffic',
    'Schools too far',
    'Trusting the builder',
    'Resale value',
    'Nothing specific',
  ],
};

/** Static fallback — prefer `buildBriefOptions()` when catalog facets are loaded. */
export const BRIEF_OPTIONS = DEFAULT_BRIEF_OPTIONS;

export interface BriefFacetsResponse {
  locations: string[];
  bhk: string[];
  property_types: string[];
  plot_sizes?: string[];
}

export type BriefOptionsMap = Partial<Record<PrefKey, readonly string[]>>;

export function buildBriefOptions(facets?: BriefFacetsResponse | null): BriefOptionsMap {
  const next: BriefOptionsMap = { ...DEFAULT_BRIEF_OPTIONS };
  if (!facets) return next;

  if (facets.bhk.length > 0) {
    next.bhk = facets.bhk;
  }
  if (facets.property_types.length > 0) {
    next.propertyType = facets.property_types;
  }
  if (facets.plot_sizes?.length) {
    next.plotSize = facets.plot_sizes;
  }
  if (facets.locations.length > 0) {
    next.location = [...facets.locations, 'Open to suggestions'];
  }
  return next;
}

/** Keys collected one chip at a time */
export const SINGLE_BRIEF_KEYS: PrefKey[] = ['purpose', 'goal', 'budget', 'location'];

/** Keys where buyer can pick multiple before continuing */
export const MULTI_BRIEF_KEYS: PrefKey[] = ['propertyType', 'bhk', 'plotSize', 'worries'];

export const CORE_BRIEF_KEYS: PrefKey[] = ['purpose', 'budget', 'propertyType', 'bhk', 'plotSize', 'location'];

export function isResidentialPropertyLabel(label: string): boolean {
  const s = label.toLowerCase();
  return s.includes('apartment') || s.includes('villa') || s.includes('flat');
}

export function isLandPropertyLabel(label: string): boolean {
  const s = label.toLowerCase();
  return (
    s.includes('plot') ||
    s.includes('land') ||
    s.includes('plantation') ||
    s.includes('planted')
  );
}

export function needsBhkStep(prefs: Preferences): boolean {
  return prefs.propertyTypes.some(isResidentialPropertyLabel);
}

export function needsPlotSizeStep(prefs: Preferences): boolean {
  return prefs.propertyTypes.some(isLandPropertyLabel);
}

/** One rule per ask: the question only exists when its predicate holds. */
type BriefRule = { key: PrefKey; when: (p: Preferences, o: BriefOptionsMap) => boolean };

/**
 * The CORE brief — the minimum viable understanding, adaptive to what the
 * buyer already said (investment briefs branch to goal+horizon; land briefs
 * to plot size; residential to bhk). Deepening asks live in DEEPEN_RULES,
 * never here — they must not block readiness.
 */
const CORE_RULES: BriefRule[] = [
  { key: 'purpose', when: () => true },
  { key: 'goal', when: (p) => p.purpose === 'Investment' },
  { key: 'horizon', when: (p) => p.purpose === 'Investment' },
  { key: 'budget', when: () => true },
  { key: 'propertyType', when: (_p, o) => (o.propertyType?.length ?? 0) > 0 },
  { key: 'bhk', when: (p, o) => needsBhkStep(p) && (o.bhk?.length ?? 0) > 0 },
  { key: 'plotSize', when: (p, o) => needsPlotSizeStep(p) && (o.plotSize?.length ?? 0) > 0 },
  { key: 'location', when: () => true },
  // The understanding half — asked last, once there's rapport. Most portals
  // never ask; it's what the trade-off ranking listens to hardest.
  { key: 'worries', when: () => true },
];

export function briefKeysFor(
  prefs: Preferences,
  options: BriefOptionsMap = DEFAULT_BRIEF_OPTIONS,
): PrefKey[] {
  return CORE_RULES.filter((r) => r.when(prefs, options)).map((r) => r.key);
}

/** Buyer buying a home to live in (or residential types picked). */
function isFamilyBrief(p: Preferences): boolean {
  return p.purpose === 'Self-use' || p.propertyTypes.some(isResidentialPropertyLabel);
}
/** Already told us schools matter via worries — don't re-ask what we know. */
function schoolsDerivable(p: Preferences): boolean {
  return p.worries.some((w) => /school/i.test(w));
}
function commuteSensitive(p: Preferences): boolean {
  return p.purpose === 'Self-use' || p.worries.some((w) => /traffic|commute/i.test(w));
}

/**
 * Optional deepening — offered AFTER the core brief completes, one at a time,
 * each with a decline chip so it reads as an offer, not a form field. Skipped
 * entirely when the answer is already derivable (the derivedPriorityFromWorries
 * pattern, client-side).
 */
const DEEPEN_RULES: BriefRule[] = [
  { key: 'schools', when: (p) => isFamilyBrief(p) && !schoolsDerivable(p) },
  { key: 'hub', when: (p) => commuteSensitive(p) },
];

export function nextDeepeningKey(
  prefs: Preferences,
  options: BriefOptionsMap = DEFAULT_BRIEF_OPTIONS,
): PrefKey | null {
  for (const r of DEEPEN_RULES) {
    if (!r.when(prefs, options)) continue;
    if (!prefs[r.key as 'schools' | 'hub']) return r.key;
  }
  return null;
}

export function isDeepeningPending(
  prefs: Preferences,
  options: BriefOptionsMap = DEFAULT_BRIEF_OPTIONS,
): boolean {
  return nextDeepeningKey(prefs, options) !== null;
}

export function briefProgress(
  prefs: Preferences,
  options: BriefOptionsMap = DEFAULT_BRIEF_OPTIONS,
): { filled: number; total: number } {
  const keys = briefKeysFor(prefs, options);
  const filled = keys.filter((k) => isPrefStepDone(prefs, k, options)).length;
  return { filled, total: keys.length };
}

export function isPrefStepDone(
  prefs: Preferences,
  key: PrefKey,
  options: BriefOptionsMap = DEFAULT_BRIEF_OPTIONS,
): boolean {
  if (key === 'propertyType' && (options.propertyType?.length ?? 0) === 0) return true;
  if (key === 'bhk' && !needsBhkStep(prefs)) return true;
  if (key === 'plotSize' && !needsPlotSizeStep(prefs)) return true;
  if (key === 'bhk') return prefs.bhkSelections.length > 0 && prefs.multiConfirmed.includes('bhk');
  if (key === 'plotSize') return prefs.plotSizeSelections.length > 0 && prefs.multiConfirmed.includes('plotSize');
  if (key === 'propertyType') return prefs.propertyTypes.length > 0 && prefs.multiConfirmed.includes('propertyType');
  if (key === 'worries') return prefs.worries.length > 0 && prefs.multiConfirmed.includes('worries');
  if ((key === 'goal' || key === 'horizon') && prefs.purpose !== 'Investment') return true;
  return Boolean(prefs[key]);
}

export function isBriefReady(
  prefs: Preferences,
  options: BriefOptionsMap = DEFAULT_BRIEF_OPTIONS,
): boolean {
  const { filled, total } = briefProgress(prefs, options);
  return filled === total;
}

export function nextBriefKey(
  prefs: Preferences,
  options: BriefOptionsMap = DEFAULT_BRIEF_OPTIONS,
): PrefKey | null {
  for (const key of briefKeysFor(prefs, options)) {
    if (!isPrefStepDone(prefs, key, options)) return key;
  }
  return null;
}

export function isMultiBriefKey(key: PrefKey): boolean {
  return MULTI_BRIEF_KEYS.includes(key);
}

export type MultiBriefPref = 'propertyType' | 'bhk' | 'plotSize' | 'worries';

export function findBriefChipPref(
  label: string,
  prefs: Preferences,
  options: BriefOptionsMap = DEFAULT_BRIEF_OPTIONS,
): { pref: PrefKey; value: string } | null {
  const trimmed = label.trim();
  for (const key of briefKeysFor(prefs, options)) {
    const opts = options[key];
    if (opts?.includes(trimmed)) return { pref: key, value: trimmed };
  }
  return null;
}

/** Only match the active brief step — prevents location chips firing during property-type step. */
export function findBriefChipPrefForStep(
  label: string,
  prefs: Preferences,
  options: BriefOptionsMap = DEFAULT_BRIEF_OPTIONS,
): { pref: PrefKey; value: string } | null {
  const trimmed = label.trim();
  const next = nextBriefKey(prefs, options);
  if (!next || isMultiBriefKey(next)) return null;
  const opts = options[next];
  if (opts?.includes(trimmed)) return { pref: next, value: trimmed };
  return null;
}

export interface BriefPreviewStep {
  /** The buyer's own words for this filter ("4 BHK", "₹50–70L", "Whitefield"). */
  label: string;
  /** Short kind tag shown after the label ("Type", "Config", "Budget", "Area"). */
  kind: string;
  /** Cumulative advisor-preferences up to AND INCLUDING this step — what to
   *  preview against so the count reflects every filter applied so far. */
  preferences: Record<string, string | undefined>;
}

/**
 * The thinking-strip's narrowing filters, in the order they resolve, each
 * carrying the CUMULATIVE preference set to preview against. Single source of
 * truth for both the strip's labels and the live match-count ladder, so the two
 * can never drift ("4 BHK · 6 → ₹60L · 0 → Devanahalli · 0"). Hard filters only
 * (type/config/budget/area) — the soft deepening answers don't narrow the count.
 */
export function briefPreviewSteps(prefs: Preferences): BriefPreviewStep[] {
  const steps: BriefPreviewStep[] = [];
  const cumulative: Record<string, string | undefined> = {};
  const push = (label: string, kind: string, patch: Record<string, string>) => {
    Object.assign(cumulative, patch);
    steps.push({ label, kind, preferences: { ...cumulative } });
  };

  if (prefs.propertyTypes.length) {
    push(prefs.propertyTypes.join(' / '), 'Type', { property_type: prefs.propertyTypes.join(', ') });
  }
  const configs = [...prefs.bhkSelections, ...prefs.plotSizeSelections];
  if (prefs.bhkSelections.length) {
    push(prefs.bhkSelections.join(', '), 'Config', { bhk: configs.join(', ') });
  } else if (prefs.plotSizeSelections.length) {
    push(prefs.plotSizeSelections.join(', '), 'Plot size', { bhk: configs.join(', ') });
  }
  if (prefs.budget) {
    push(prefs.budget, 'Budget', { budget: prefs.budget });
  }
  if (prefs.location) {
    // "Open to suggestions" still shows as a row, but the Spine treats it as no
    // area filter — so the count legitimately holds rather than narrowing.
    push(prefs.location, 'Area', { location: prefs.location });
  }
  return steps;
}

/** Structured payload for ConverseSpine — avoids re-parsing chip labels from NL. */
export function prefsToAdvisorPreferences(prefs: Preferences): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  if (prefs.purpose) {
    out.purpose =
      prefs.purpose === 'Investment'
        ? 'investment'
        : prefs.purpose === 'Self-use'
          ? 'self_use'
          : prefs.purpose.toLowerCase();
  }
  if (prefs.budget) out.budget = prefs.budget;
  out.location = prefs.location ?? '';
  if (prefs.propertyTypes.length) out.property_type = prefs.propertyTypes.join(', ');
  const configs = [...prefs.bhkSelections, ...prefs.plotSizeSelections];
  if (configs.length) out.bhk = configs.join(', ');
  else if (prefs.bhk) out.bhk = prefs.bhk;
  else if (needsBhkStep(prefs)) out.bhk = '';
  const realWorries = prefs.worries.filter((w) => w !== 'Nothing specific');
  if (realWorries.length) out.worries = realWorries.join(', ');
  // Deepening answers — decline chips are opt-outs, never signals.
  if (prefs.hub && !/not commute/i.test(prefs.hub)) out.commute_hub = prefs.hub;
  if (prefs.schools && /^yes/i.test(prefs.schools)) out.schools = 'important';
  return out;
}

/** Single composed turn for ConverseSpine after the prefs panel is complete. */
export function formatBriefForBot(prefs: Preferences): string {
  const parts: string[] = [];
  if (prefs.purpose) {
    parts.push(prefs.purpose === 'Self-use' ? 'self-use home' : prefs.purpose);
  }
  if (prefs.goal) parts.push(prefs.goal);
  if (prefs.horizon) parts.push(`${prefs.horizon} horizon`);
  if (prefs.budget) parts.push(`budget ${prefs.budget}`);
  if (prefs.propertyTypes.length) {
    parts.push(`looking for ${prefs.propertyTypes.join(' or ')}`);
  }
  const configs = [...prefs.bhkSelections, ...prefs.plotSizeSelections];
  if (configs.length) parts.push(configs.join(' or '));
  if (prefs.location === 'Open to suggestions') {
    parts.push('open to any area');
  } else if (prefs.location) {
    parts.push(`in ${prefs.location}`);
  }
  return parts.join(', ');
}

export function emptyPreferences(): Preferences {
  return {
    purpose: null,
    budget: null,
    bhk: null,
    location: null,
    timeline: null,
    goal: null,
    bhkSelections: [],
    propertyTypes: [],
    plotSizeSelections: [],
    worries: [],
    multiConfirmed: [],
    horizon: null,
    schools: null,
    hub: null,
  };
}

export function pruneConfigSelections(prefs: Preferences): Preferences {
  const next = { ...prefs };
  if (!needsBhkStep(next)) {
    next.bhkSelections = [];
    next.bhk = null;
  }
  if (!needsPlotSizeStep(next)) {
    next.plotSizeSelections = [];
  }
  return next;
}
